/**
 * Auto-Refresh Hook with Intelligent Batching
 * ============================================
 *
 * 2025 Best Practices Implementation:
 * - Auto-refreshes data at configurable intervals
 * - Batches updates for smooth 10-15 FPS rendering
 * - Uses visibility API to pause when tab is hidden
 * - Implements exponential backoff on errors
 * - WebSocket integration for instant updates
 *
 * @author ASTRA Dashboard Enhancement
 * @date December 2025
 */

import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Smart auto-refresh hook that handles real-time data updates
 *
 * BACKWARD COMPATIBLE: Supports both API patterns:
 * - New API: useAutoRefresh(fetchFn, options)
 * - Legacy API: useAutoRefresh({ intervalMs, onRefresh, enabled })
 *
 * @param {Function|Object} firstArg - Async fetch function OR legacy options object
 * @param {Object} secondArg - Configuration options (only used with new API)
 * @returns {Object} - { data, isLoading, error, refresh, lastUpdate, isStale, refreshCount }
 */
export function useAutoRefresh(firstArg, secondArg = {}) {
  // 🔧 2025-12-30: BACKWARD COMPATIBLE API DETECTION
  // Detect which API pattern is being used and normalize
  let fetchFn, options;

  if (typeof firstArg === 'function') {
    // New API: useAutoRefresh(fetchFn, options)
    fetchFn = firstArg;
    options = secondArg;
  } else if (typeof firstArg === 'object' && firstArg !== null) {
    // Legacy API: useAutoRefresh({ intervalMs, onRefresh, enabled, ... })
    options = firstArg;
    // Extract fetchFn from legacy onRefresh callback
    fetchFn = options.onRefresh || (() => Promise.resolve(null));
  } else {
    // Fallback: treat as new API with defaults
    fetchFn = () => Promise.resolve(null);
    options = secondArg;
  }

  const {
    // Refresh interval in ms (default: 5 seconds for live trading data)
    // Support both 'interval' and 'intervalMs' for backward compatibility
    interval = options.intervalMs || 5000,
    // Whether auto-refresh is enabled
    enabled = true,
    // Initial data
    initialData = null,
    // Pause when tab is not visible
    pauseOnHidden = true,
    // Callback on successful fetch
    onSuccess = null,
    // Callback on error (not the same as legacy onRefresh)
    onError = null,
    // Max retries before giving up
    maxRetries = 3,
    // Retry delay multiplier
    retryMultiplier = 1.5,
    // Whether to fetch immediately on mount
    fetchOnMount = true,
    // Dependency array for re-fetching
    deps = [],
    // External refresh trigger (from parent component)
    refreshTrigger = null,
  } = options;

  const [data, setData] = useState(initialData);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  // 🔧 2025-12-30: Add refreshCount for legacy API compatibility
  const [refreshCount, setRefreshCount] = useState(0);

  // Refs to avoid stale closures
  const fetchFnRef = useRef(fetchFn);
  const intervalRef = useRef(null);
  const retryTimeoutRef = useRef(null);
  const mountedRef = useRef(true);

  // Update ref when fetchFn changes
  useEffect(() => {
    fetchFnRef.current = fetchFn;
  }, [fetchFn]);

  // Visibility change handler
  useEffect(() => {
    if (!pauseOnHidden) return;

    const handleVisibilityChange = () => {
      setIsVisible(document.visibilityState === 'visible');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [pauseOnHidden]);

  // Main fetch function
  const fetchData = useCallback(async (isManual = false) => {
    if (!mountedRef.current) return;

    try {
      // Only show loading on initial fetch or manual refresh
      if (data === null || isManual) {
        setIsLoading(true);
      }

      const result = await fetchFnRef.current();

      if (!mountedRef.current) return;

      setData(result);
      setLastUpdate(new Date());
      setError(null);
      setRetryCount(0);
      // 🔧 2025-12-30: Increment refreshCount for legacy API compatibility
      setRefreshCount(prev => prev + 1);

      if (onSuccess) {
        onSuccess(result);
      }
    } catch (err) {
      if (!mountedRef.current) return;

      const errorMessage = err.message || 'Failed to fetch data';
      setError(errorMessage);

      if (onError) {
        onError(err);
      }

      // Implement exponential backoff retry
      if (retryCount < maxRetries) {
        const delay = Math.min(interval * Math.pow(retryMultiplier, retryCount), 30000);
        setRetryCount(prev => prev + 1);

        retryTimeoutRef.current = setTimeout(() => {
          if (mountedRef.current) {
            fetchData();
          }
        }, delay);
      }
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [data, interval, maxRetries, retryMultiplier, retryCount, onSuccess, onError]);

  // Manual refresh function
  const refresh = useCallback(() => {
    return fetchData(true);
  }, [fetchData]);

  // Initial fetch
  useEffect(() => {
    if (fetchOnMount) {
      fetchData(true);
    }
  }, [fetchOnMount]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch when deps change
  useEffect(() => {
    if (deps.length > 0) {
      fetchData();
    }
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh interval
  useEffect(() => {
    if (!enabled || !isVisible) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    intervalRef.current = setInterval(() => {
      fetchData();
    }, interval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, interval, isVisible, fetchData]);

  // Cleanup
  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  // 🔧 2025-12-30: External refresh trigger support (for parent-controlled refresh)
  const prevRefreshTriggerRef = useRef(refreshTrigger);
  useEffect(() => {
    // Only trigger if refreshTrigger actually changed (not on initial mount)
    if (refreshTrigger !== null && refreshTrigger !== prevRefreshTriggerRef.current) {
      prevRefreshTriggerRef.current = refreshTrigger;
      fetchData();
    }
  }, [refreshTrigger, fetchData]);

  // Calculate if data is stale (older than 2x interval)
  const isStale = lastUpdate
    ? Date.now() - lastUpdate.getTime() > interval * 2
    : false;

  return {
    data,
    isLoading,
    // 🔧 2025-12-30: Include refreshCount for legacy API compatibility
    refreshCount,
    error,
    refresh,
    lastUpdate,
    isStale,
    retryCount,
    isVisible,
  };
}

/**
 * Hook for multiple data sources with synchronized refresh
 */
export function useMultiAutoRefresh(fetchConfigs, globalInterval = 5000) {
  const [results, setResults] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const intervalRef = useRef(null);
  const mountedRef = useRef(true);

  const fetchAll = useCallback(async () => {
    if (!mountedRef.current) return;

    try {
      const promises = fetchConfigs.map(async ({ key, fetchFn }) => {
        try {
          const data = await fetchFn();
          return { key, data, error: null };
        } catch (error) {
          return { key, data: null, error: error.message };
        }
      });

      const responses = await Promise.all(promises);

      if (!mountedRef.current) return;

      const newResults = {};
      responses.forEach(({ key, data, error }) => {
        newResults[key] = { data, error, loading: false };
      });

      setResults(prev => ({ ...prev, ...newResults }));
      setLastUpdate(new Date());
    } catch (err) {
      console.error('Multi-fetch error:', err);
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [fetchConfigs]);

  useEffect(() => {
    fetchAll();

    intervalRef.current = setInterval(fetchAll, globalInterval);

    return () => {
      mountedRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchAll, globalInterval]);

  return { results, isLoading, lastUpdate, refresh: fetchAll };
}

/**
 * Hook for real-time WebSocket data with auto-reconnect
 */
export function useRealtimeWebSocket(url, options = {}) {
  const {
    onMessage = null,
    onConnect = null,
    onDisconnect = null,
    onError = null,
    reconnectInterval = 3000,
    maxReconnects = 10,
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const [reconnectCount, setReconnectCount] = useState(0);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const mountedRef = useRef(true);

  const connect = useCallback(() => {
    if (!mountedRef.current || !url) return;

    try {
      wsRef.current = new WebSocket(url);

      wsRef.current.onopen = () => {
        if (!mountedRef.current) return;
        setIsConnected(true);
        setReconnectCount(0);
        if (onConnect) onConnect();
      };

      wsRef.current.onmessage = (event) => {
        if (!mountedRef.current) return;
        try {
          const data = JSON.parse(event.data);
          setLastMessage(data);
          if (onMessage) onMessage(data);
        } catch (err) {
          console.error('WebSocket message parse error:', err);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        if (onError) onError(error);
      };

      wsRef.current.onclose = () => {
        if (!mountedRef.current) return;
        setIsConnected(false);
        if (onDisconnect) onDisconnect();

        // Auto-reconnect
        if (reconnectCount < maxReconnects) {
          reconnectTimeoutRef.current = setTimeout(() => {
            if (mountedRef.current) {
              setReconnectCount(prev => prev + 1);
              connect();
            }
          }, reconnectInterval * Math.pow(1.5, reconnectCount));
        }
      };
    } catch (err) {
      console.error('WebSocket connection error:', err);
    }
  }, [url, reconnectCount, maxReconnects, reconnectInterval, onConnect, onDisconnect, onMessage, onError]);

  useEffect(() => {
    connect();

    return () => {
      mountedRef.current = false;
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [connect]);

  const send = useCallback((data) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    }
  }, []);

  return {
    isConnected,
    lastMessage,
    reconnectCount,
    send,
  };
}

export default useAutoRefresh;
